import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { fetchPetHistory } from '../utils/api';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const HistoryChart = () => {
  const [historyData, setHistoryData] = useState({
    labels: [],
    datasets: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch real historical data from the API
    async function fetchHistoricalData() {
      try {
        setLoading(true);
        const historyData = await fetchPetHistory();
        
        if (historyData && historyData.length > 0) {
          processHistoryData(historyData);
        } else {
          // Fallback to mock data if no real data is available
          generateMockHistoryData();
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching history data:', err);
        setError('Failed to fetch history data');
        // Fallback to mock data on error
        generateMockHistoryData();
        setLoading(false);
      }
    }
    
    fetchHistoricalData();
    
    // Set up polling interval (every 5 minutes)
    const intervalId = setInterval(fetchHistoricalData, 300000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, []);

  // Process real history data from API
  const processHistoryData = (historyData) => {
    // Sort data by timestamp (oldest first)
    const sortedData = [...historyData].sort((a, b) => a.timestamp - b.timestamp);
    
    // Extract timestamps for labels
    const labels = sortedData.map(entry => {
      const date = new Date(entry.timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    });
    
    // Extract stat values
    const hungerData = sortedData.map(entry => entry.stats.hunger);
    const energyData = sortedData.map(entry => entry.stats.energy);
    const happinessData = sortedData.map(entry => entry.stats.happiness);
    
    setHistoryData({
      labels,
      datasets: [
        {
          label: 'HUNGER',
          data: hungerData,
          borderColor: 'rgb(140, 101, 247)', // primary-500
          backgroundColor: 'rgba(140, 101, 247, 0.1)',
          borderWidth: 2,
          pointStyle: 'rect',
          pointRadius: 3,
          pointBackgroundColor: 'rgb(140, 101, 247)',
          tension: 0.1,
          stepped: 'before'
        },
        {
          label: 'ENERGY',
          data: energyData,
          borderColor: 'rgb(0, 194, 255)', // accent-500
          backgroundColor: 'rgba(0, 194, 255, 0.1)',
          borderWidth: 2,
          pointStyle: 'rect',
          pointRadius: 3,
          pointBackgroundColor: 'rgb(0, 194, 255)',
          tension: 0.1,
          stepped: 'before'
        },
        {
          label: 'HAPPINESS',
          data: happinessData,
          borderColor: 'rgb(255, 204, 0)', // warning-500
          backgroundColor: 'rgba(255, 204, 0, 0.1)',
          borderWidth: 2,
          pointStyle: 'rect',
          pointRadius: 3,
          pointBackgroundColor: 'rgb(255, 204, 0)',
          tension: 0.1,
          stepped: 'before'
        }
      ]
    });
  };

  // Fallback to generate mock data if API fails
  const generateMockHistoryData = () => {
    // Generate timestamps for the last 24 hours (one per hour)
    const now = new Date();
    const labels = Array.from({ length: 24 }, (_, i) => {
      const time = new Date(now);
      time.setHours(now.getHours() - 23 + i);
      return time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    });
    
    // Generate mock data for each stat
    const hungerData = generateMockStatData(70, 90);
    const energyData = generateMockStatData(60, 85);
    const happinessData = generateMockStatData(75, 95);
    
    setHistoryData({
      labels,
      datasets: [
        {
          label: 'HUNGER',
          data: hungerData,
          borderColor: 'rgb(140, 101, 247)', // primary-500
          backgroundColor: 'rgba(140, 101, 247, 0.1)',
          borderWidth: 2,
          pointStyle: 'rect',
          pointRadius: 3,
          pointBackgroundColor: 'rgb(140, 101, 247)',
          tension: 0.1,
          stepped: 'before'
        },
        {
          label: 'ENERGY',
          data: energyData,
          borderColor: 'rgb(0, 194, 255)', // accent-500
          backgroundColor: 'rgba(0, 194, 255, 0.1)',
          borderWidth: 2,
          pointStyle: 'rect',
          pointRadius: 3,
          pointBackgroundColor: 'rgb(0, 194, 255)',
          tension: 0.1,
          stepped: 'before'
        },
        {
          label: 'HAPPINESS',
          data: happinessData,
          borderColor: 'rgb(255, 204, 0)', // warning-500
          backgroundColor: 'rgba(255, 204, 0, 0.1)',
          borderWidth: 2,
          pointStyle: 'rect',
          pointRadius: 3,
          pointBackgroundColor: 'rgb(255, 204, 0)',
          tension: 0.1,
          stepped: 'before'
        }
      ]
    });
  };
  
  // Helper to generate random stat data with a trend
  const generateMockStatData = (min, max) => {
    let value = Math.floor(Math.random() * (max - min)) + min;
    
    return Array.from({ length: 24 }, () => {
      // Random walk with bounds
      const change = Math.floor(Math.random() * 7) - 3;
      value = Math.max(min, Math.min(max, value + change));
      return value;
    });
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            family: "'Press Start 2P', monospace",
            size: 8
          },
          color: '#a78bfa', // primary-400
          boxWidth: 10,
          boxHeight: 10,
          padding: 10
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: '#1f2937', // dark-800
        titleFont: {
          family: "'Press Start 2P', monospace",
          size: 8
        },
        bodyFont: {
          family: "monospace",
          size: 10
        },
        borderColor: '#8c65f7', // primary-500
        borderWidth: 1,
        padding: 8,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(75, 85, 99, 0.2)', // gray-600 with opacity
          borderDash: [2, 2]
        },
        ticks: {
          font: {
            family: "monospace",
            size: 8
          },
          color: '#a78bfa', // primary-400
          stepSize: 20
        },
        title: {
          display: true,
          text: 'VALUE (%)',
          font: {
            family: "'Press Start 2P', monospace",
            size: 8
          },
          color: '#a78bfa' // primary-400
        }
      },
      x: {
        grid: {
          color: 'rgba(75, 85, 99, 0.2)', // gray-600 with opacity
          borderDash: [2, 2]
        },
        ticks: {
          font: {
            family: "monospace",
            size: 8
          },
          color: '#a78bfa', // primary-400
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 8
        },
        title: {
          display: true,
          text: 'TIME',
          font: {
            family: "'Press Start 2P', monospace",
            size: 8
          },
          color: '#a78bfa' // primary-400
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'linear'
    },
    elements: {
      line: {
        borderJoinStyle: 'miter'
      }
    }
  };

  if (loading && historyData.labels.length === 0) {
    return (
      <div className="h-full w-full bg-dark-900 p-2 rounded-sm flex items-center justify-center">
        <div className="text-primary-400 font-pixel text-sm">Loading chart data...</div>
      </div>
    );
  }

  if (error && historyData.labels.length === 0) {
    return (
      <div className="h-full w-full bg-dark-900 p-2 rounded-sm flex items-center justify-center">
        <div className="text-danger-400 font-pixel text-sm">Error loading chart data</div>
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-dark-900 p-2 rounded-sm">
      <Line data={historyData} options={options} />
    </div>
  );
};

export default HistoryChart; 